import { useState, useEffect } from 'react'
import { getInitials } from '../constants'
import { BASE_SHARES_URL, ROLE, isAdminRole } from '../constants'
import { useThemeColor } from '../../hooks/useThemeColor'
import { useOrganizationLogo } from '../../hooks/useOrganizationLogo'
import { useNavigate, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { fetchChatHistoryByUser } from '../../slice/api/historySlice'
import { Home, Layers, Bell, MoreHorizontal, BrainCircuit, LifeBuoy } from 'lucide-react'
import { useTranslation } from '../../i18n/LanguageContext';

const Nav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { data } = useSelector((state) => state.admin);
  const { userHistories = [], userHistoriesStatus } = useSelector((state) => state.history);
  const { aispaceColor } = useThemeColor();
  const { logoUrl, loading } = useOrganizationLogo();
  const { t } = useTranslation();

  const [showLogout, setShowLogout] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const token = localStorage.getItem('token');
  // Persist collapsed state across navigation so pages mounted later (e.g.
  // Analysis) can read the same state and not assume the default expanded
  // width.
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try { return localStorage.getItem('navCollapsed') === '1'; } catch { return false; }
  });
  const [pinnedProjects, setPinnedProjects] = useState([]);
  const [sharedHistoryIds, setSharedHistoryIds] = useState(new Set());

  const user_id = localStorage.getItem('id');
  const storedName = localStorage.getItem("name");
  const name = data?.user_fullname || (storedName && storedName !== "undefined" ? storedName : "");
  const email = localStorage.getItem("email");
  const initials = getInitials(name);

  // Fetch chat history for recent projects — once. History.jsx/HomeScreen.jsx's
  // create/rename/delete actions patch this same store directly now, so Nav
  // (mounted on every route) no longer needs to refetch on every navigation.
  useEffect(() => {
    if (user_id && userHistoriesStatus !== 'succeeded' && userHistoriesStatus !== 'loading') {
      dispatch(fetchChatHistoryByUser(user_id));
    }
  }, [dispatch, user_id, userHistoriesStatus]);

  const fetchSharedIds = () => {
    const token = localStorage.getItem('token')
    if (!token) return
    fetch(`${BASE_SHARES_URL}/my-shares`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { history_ids: [] })
      .then(d => setSharedHistoryIds(new Set((d.history_ids || []).map(String))))
      .catch(() => {})
  }

  useEffect(() => {
    fetchSharedIds()
    window.addEventListener('sharesUpdated', fetchSharedIds)
    return () => window.removeEventListener('sharesUpdated', fetchSharedIds)
  }, []);


  // Load pinned projects from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('pinnedProjects');
    if (stored) {
      setPinnedProjects(JSON.parse(stored));
    }
  }, []);

  // ── SSE: single persistent connection replaces all polling ──────────────────
  useEffect(() => {
    if (!token) return

    const url = `${BASE_SHARES_URL}/notifications/stream?token=${encodeURIComponent(token)}`
    const es = new EventSource(url)

    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data)
        if (data.count !== undefined) setUnreadCount(data.count)
      } catch { /* ignore malformed frames */ }
    }

    es.onerror = () => {
      // EventSource is neither axios nor fetch, so the global 401 handler in
      // authInterceptor.js never sees this connection — an expired session
      // would just stop the bell updating, silently.
      //
      // onerror carries no status code, but the two cases are distinguishable
      // by readyState: a dropped connection leaves it CONNECTING and the
      // browser retries on its own, while a non-2xx response (a 401 here) is
      // fatal per spec and leaves it CLOSED with no retry. On the fatal case,
      // probe a normal endpoint — that request DOES go through the fetch
      // wrapper, so the redirect decision stays in one place instead of being
      // reimplemented here. A rejected probe means offline, not unauthorized.
      if (es.readyState === EventSource.CLOSED) {
        fetch(`${BASE_SHARES_URL}/notifications/unread-count`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => { /* network down — not an auth failure */ })
      }
    }

    return () => es.close()
  }, [token])

  // ── Same-tab updates from Notifications page (mark read actions) ─────────────
  useEffect(() => {
    const handler = (e) => setUnreadCount(e.detail.count)
    window.addEventListener('notifCountChange', handler)
    return () => window.removeEventListener('notifCountChange', handler)
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const profileElement = document.getElementById('profile-dropdown');
      if (profileElement && !profileElement.contains(event.target) && showLogout) {
        setShowLogout(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLogout]);

  const logout = () => {
    localStorage.clear();
    window.location.href = '/login';
  };

  const isAdminOrSuperAdmin = isAdminRole(data?.user_role);

  // Main navigation routes
  const mainRoutes = [
    ...(data?.user_role !== ROLE.NO_LICENSE ? [{
      path: "/",
      text: t('nav.homepage'),
      icon: Home
    }] : []),
    ...(data?.user_role !== ROLE.NO_LICENSE ? [{
      path: "/history",
      text: t('nav.projects'),
      icon: Layers
    }] : []),
    // Agents only for ADMIN / SUPER_ADMIN — normal users do not see this tab
    ...(isAdminOrSuperAdmin ? [{
      path: "/agents",
      text: t('nav.agents'),
      icon: BrainCircuit
    }] : []),
  ];

  const isNoLicense = data?.user_role === ROLE.NO_LICENSE;

  // Bottom navigation routes — notifications hidden for No License users
  const bottomRoutes = [
    ...(!isNoLicense ? [{
      path: "/notifications",
      text: t('nav.notifications'),
      icon: Bell
    }] : []),
    {
      path: "/hilfe",
      text: t('nav.helpCenter'),
      icon: LifeBuoy
    },
  ];

  const navigateToRoute = (route) => {
    if (route.disabled) return;

    if (data?.user_role === ROLE.NO_LICENSE && route.path !== '/hilfe') {
      navigate('/hilfe');
      return;
    }

    if (window.location.pathname === route.path) {
      window.location.reload();
    } else {
      navigate(route.path);
    }
  };

  // Get recent projects (last 3, sorted by most recent activity — a chat
  // someone is actively using surfaces here even if created a while ago,
  // not just whichever was CREATED most recently). Falls back to timestamp
  // (creation time) for older rows from before last_activity_at existed.
  const recentProjects = Array.isArray(userHistories)
    ? [...userHistories]
        .sort((a, b) => {
          const dateA = new Date(a.last_activity_at || a.timestamp || 0);
          const dateB = new Date(b.last_activity_at || b.timestamp || 0);
          return dateB - dateA;
        })
        .slice(0, 3)
        .map(item => ({
          id: item.id,
          name: item.history_name || item.task_name || 'Unbenanntes Projekt'
        }))
    : [];

  // Get pinned project details
  const pinnedProjectDetails = pinnedProjects
    .map(id => {
      const project = userHistories.find(p => p.id === id);
      return project ? { id: project.id, name: project.history_name || project.task_name || 'Unbenanntes Projekt' } : null;
    })
    .filter(Boolean);

  // Toggle sidebar collapse — persist to localStorage so pages mounted later
  // can initialise their navWidth correctly.
  const toggleSidebar = () => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    try { localStorage.setItem('navCollapsed', next ? '1' : '0'); } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent('sidebarToggle', { detail: { isCollapsed: next } }));
  };

  return (
    <aside
      className={`fixed top-0 left-0 bottom-0 bg-white border-r border-[#E5E7EB] flex flex-col z-[110] transition-all duration-300 ${
        isCollapsed ? 'w-[72px]' : 'w-[280px]'
      }`}
    >
      {/* Logo Section */}
      <div className={`flex items-center ${isCollapsed ? 'justify-center px-3' : 'justify-between px-5'} py-4 min-h-[72px]`}>
        {!isCollapsed && (
          <div
            className='flex items-center gap-3 cursor-pointer'
            onClick={() => {
              if (data?.user_role === ROLE.NO_LICENSE) {
                navigate('/hilfe');
              } else {
                navigate('/');
              }
            }}
          >
            {loading ? (
              <div className="relative w-10 h-10">
                <div className="absolute inset-0 border-2 rounded-lg animate-ping opacity-75" style={{ borderColor: aispaceColor }}></div>
              </div>
            ) : (
              <>
                {/* Dynamic logo from organization */}
                <img src={logoUrl} alt="Logo" className="h-10 w-auto object-contain" />
              </>
            )}
          </div>
        )}
        <button
          onClick={toggleSidebar}
          className='p-2 hover:bg-[#F3F4F6] rounded-lg transition-colors'
          title={isCollapsed ? 'Seitenleiste erweitern' : 'Seitenleiste einklappen'}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="3" width="18" height="18" rx="2" stroke="#9CA3AF" strokeWidth="1.5"/>
            <path d="M9 3V21" stroke="#9CA3AF" strokeWidth="1.5"/>
          </svg>
        </button>
      </div>

      {/* Main Navigation */}
      <nav className='flex-1 overflow-y-auto px-3'>
        <div className='space-y-1'>
          {mainRoutes.map((route, index) => {
            const isActive = location.pathname === route.path;
            const IconComponent = route.icon;
            return (
              <button
                key={index}
                onClick={() => navigateToRoute(route)}
                className={`w-full flex items-center ${isCollapsed ? 'justify-center' : ''} gap-3 px-3 py-2.5 rounded-lg text-[14px] font-medium transition-colors ${
                  isActive
                    ? 'bg-[#F3F4F6] text-[#111827]'
                    : 'text-[#374151] hover:bg-[#F9FAFB]'
                }`}
                title={isCollapsed ? route.text : ''}
              >
                <IconComponent size={20} strokeWidth={1.8} className={isActive ? 'text-[#111827]' : 'text-[#6B7280]'} />
                {!isCollapsed && <span>{route.text}</span>}
              </button>
            );
          })}
        </div>

        {/* Pinned Section */}
        {!isCollapsed && pinnedProjectDetails.length > 0 && (
          <div className='mt-6 pt-5 border-t border-[#E5E7EB]'>
            <h3 className='px-3 text-[12px] font-medium text-[#9CA3AF] mb-2'>
              Angepinnt
            </h3>
            <div className='space-y-0.5'>
              {pinnedProjectDetails.map((project) => {
                const isActiveProject = location.pathname === `/analysis/${project.id}`;
                return (
                  <button
                    key={project.id}
                    onClick={() => navigate(`/analysis/${project.id}`)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-[14px] transition-colors ${
                      isActiveProject
                        ? 'bg-[#F3F4F6] text-[#111827] font-medium'
                        : 'text-[#374151] hover:bg-[#F9FAFB]'
                    }`}
                  >
                    <span className='truncate'>{project.name}</span>
                    {sharedHistoryIds.has(String(project.id)) && (
                      <span className='text-[10px] font-semibold px-1.5 py-0.5 rounded-md flex-shrink-0 ml-2'
                        style={{ backgroundColor: `${aispaceColor}18`, color: aispaceColor }}>
                        {t('history.accessViewOnly')}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Recent Projects Section — hidden for No License users */}
        {!isCollapsed && !isNoLicense && recentProjects.length > 0 && (
          <div className='mt-5 pt-5 border-t border-[#E5E7EB]'>
            <h3 className='px-3 text-[12px] font-medium text-[#9CA3AF] mb-2'>
              {t('nav.recentProjects')}
            </h3>
            <div className='space-y-0.5'>
              {recentProjects.map((project) => {
                const isActiveProject = location.pathname === `/analysis/${project.id}`;
                return (
                  <button
                    key={project.id}
                    onClick={() => navigate(`/analysis/${project.id}`)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-[14px] transition-colors ${
                      isActiveProject
                        ? 'bg-[#F3F4F6] text-[#111827] font-medium'
                        : 'text-[#374151] hover:bg-[#F9FAFB]'
                    }`}
                  >
                    <span className='truncate'>{project.name}</span>
                    {sharedHistoryIds.has(String(project.id)) && (
                      <span className='text-[10px] font-semibold px-1.5 py-0.5 rounded-md flex-shrink-0 ml-2'
                        style={{ backgroundColor: `${aispaceColor}18`, color: aispaceColor }}>
                        {t('history.accessViewOnly')}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </nav>

      {/* Bottom Section */}
      <div className='border-t border-[#E5E7EB] px-3 py-4'>
        {/* Bottom Navigation */}
        <div className='space-y-1 mb-4'>
          {bottomRoutes.map((route, index) => {
            const isActive = location.pathname === route.path;
            const IconComponent = route.icon;
            const isNotifications = route.path === '/notifications';
            const showBadge = isNotifications && unreadCount > 0;
            return (
              <button
                key={index}
                onClick={() => navigateToRoute(route)}
                className={`w-full flex items-center ${isCollapsed ? 'justify-center' : ''} gap-3 px-3 py-2.5 rounded-lg text-[14px] font-medium transition-colors ${
                  isActive
                    ? 'bg-[#F3F4F6] text-[#111827]'
                    : 'text-[#374151] hover:bg-[#F9FAFB]'
                }`}
                title={isCollapsed ? route.text : ''}
              >
                {/* Icon — dot badge only when sidebar is collapsed */}
                <div className="relative flex-shrink-0">
                  <IconComponent size={20} strokeWidth={1.8} className={isActive ? 'text-[#111827]' : 'text-[#6B7280]'} />
                  {showBadge && isCollapsed && (
                    <span
                      className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 rounded-full text-white text-[9px] font-bold flex items-center justify-center px-[3px]"
                      style={{ backgroundColor: '#EF4444' }}
                    >
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </div>
                {/* Label + count chip when sidebar is expanded */}
                {!isCollapsed && <span className="flex-1 text-left">{route.text}</span>}
                {!isCollapsed && showBadge && (
                  <span
                    className="min-w-[20px] h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center px-1.5"
                    style={{ backgroundColor: '#EF4444' }}
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* User Profile */}
        <div className="relative" id="profile-dropdown">
          <button
            onClick={() => setShowLogout(!showLogout)}
            className={`w-full flex items-center ${isCollapsed ? 'justify-center' : ''} gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F9FAFB] transition-colors`}
            title={isCollapsed ? name : ''}
          >
            {/* User Avatar - using a placeholder image style */}
            <div
              className='w-9 h-9 flex items-center justify-center rounded-full text-white font-semibold text-[13px] flex-shrink-0'
              style={{ backgroundColor: aispaceColor }}
            >
              {initials}
            </div>
            {!isCollapsed && (
              <>
                <div className='flex-1 text-left min-w-0'>
                  <p className='text-[14px] font-medium text-[#111827] truncate'>{name}</p>
                  <p className='text-[12px] text-[#6B7280] truncate'>{email}</p>
                </div>
                <span className='flex-shrink-0 p-1.5 border border-[#E5E7EB] rounded-lg'>
                  <MoreHorizontal size={16} className='text-[#9CA3AF]' />
                </span>
              </>
            )}
          </button>

          {/* User Dropdown */}
          {showLogout && (
            <div className={`absolute bottom-full ${isCollapsed ? 'left-full ml-2' : 'left-0 right-0'} mb-2 bg-white border border-[#E5E7EB] rounded-xl shadow-lg overflow-hidden min-w-[260px]`}>
              {/* User info header */}
              <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E5E7EB]">
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-full text-white font-semibold text-[13px] flex-shrink-0"
                  style={{ backgroundColor: aispaceColor }}
                >
                  {initials}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-semibold text-[#111827] truncate">{name}</p>
                  <p className="text-[13px] text-[#6B7280] truncate">{email}</p>
                </div>
              </div>

              {/* Menu items */}
              <div className="p-1.5 space-y-0.5">
                {isAdminOrSuperAdmin && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowLogout(false);
                      navigate('/account');
                    }}
                    className="w-full px-3 py-2.5 flex items-center gap-3 rounded-lg hover:bg-[#F9FAFB] text-[14px] text-[#111827] transition-colors"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M9.5 12l1.8 1.8L14.5 10" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>{t('nav.account')}</span>
                  </button>
                )}
                {(isAdminRole(data?.user_role)) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowLogout(false);
                      navigate('/settings');
                    }}
                    className="w-full px-3 py-2.5 flex items-center gap-3 rounded-lg bg-[#F9FAFB] hover:bg-[#F3F4F6] text-[14px] text-[#111827] transition-colors"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.0113 9.77251C4.28059 9.5799 4.48572 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>{t('nav.settings')}</span>
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowLogout(false);
                    setShowLogoutModal(true);
                  }}
                  className="w-full px-3 py-2.5 flex items-center gap-3 rounded-lg hover:bg-[#F9FAFB] text-[14px] text-[#111827] transition-colors"
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3.75 15.75C3.3375 15.75 2.98438 15.6031 2.69063 15.3094C2.39687 15.0156 2.25 14.6625 2.25 14.25V3.75C2.25 3.3375 2.39687 2.98438 2.69063 2.69063C2.98438 2.39687 3.3375 2.25 3.75 2.25H9V3.75H3.75V14.25H9V15.75H3.75ZM12 12.75L10.9688 11.6625L12.8813 9.75H6.75V8.25H12.8813L10.9688 6.3375L12 5.25L15.75 9L12 12.75Z" fill="#6B7280"/>
                  </svg>
                  <span>{t('nav.logout')}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div
          className="fixed inset-0 bg-black/30 flex items-center justify-center z-[100]"
          onClick={() => setShowLogoutModal(false)}
        >
          <div
            className="bg-white rounded-2xl w-[420px] shadow-xl px-6 pt-8 pb-6 mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-[#FEE2E2] flex items-center justify-center">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 17L21 12L16 7" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M21 12H9" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-[20px] font-bold text-[#111827] text-center mb-2">{t('nav.logout')}</h2>

            {/* Description */}
            <p className="text-[14px] text-[#6B7280] text-center mb-6">
              {t('nav.logoutConfirm')}
            </p>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="flex-1 px-4 py-3 text-[14px] font-semibold text-[#374151] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#F9FAFB] transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => {
                  setShowLogoutModal(false);
                  logout();
                }}
                className="flex-1 px-4 py-3 text-[14px] font-semibold text-white bg-[#DC2626] rounded-lg hover:bg-[#B91C1C] transition-colors"
              >
                {t('nav.logout')}
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  )
}

export default Nav
